import { CheckCircle2 } from 'lucide-react';
import { useState } from 'react';
import { browserTimeZone, supportedTimeZones, zonedTimeToUtcIso } from '../../utils/scheduleTime';
import CountrySelect from '../CountrySelect';
import Modal from '../ui/Modal';
import { SOURCE_OPTIONS, senderProblem } from './campaignWizardState';
import { MAY_HAVE_STARTED } from './useCampaignDraft';

const fieldClass = 'w-full px-3 py-2 text-base bg-card border border-input rounded-lg';
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const TIME_ZONES = supportedTimeZones();

/** The last-chance dialog: only its confirm button starts or schedules the campaign. */
const ConfirmSend = ({ count, templateName, campaignName, sending, scheduleLabel, onCancel, onConfirm }) => (
  <Modal onClose={sending ? undefined : onCancel} maxWidth="md" fullHeight={false}>
    <div className="max-h-[80vh] overflow-y-auto p-6 space-y-4">
      <h3 className="text-lg font-medium text-foreground">
        {scheduleLabel ? 'Schedule this campaign?' : 'Send this campaign?'}
      </h3>
      <p className="text-base font-medium text-foreground">This will message {plural(count, 'person', 'people')}.</p>
      <dl className="text-sm text-muted-foreground space-y-1">
        <div className="flex gap-2">
          <dt className="shrink-0">Campaign:</dt>
          <dd className="break-words min-w-0">{campaignName}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="shrink-0">Template:</dt>
          <dd className="break-words min-w-0">{templateName}</dd>
        </div>
        {scheduleLabel && (
          <div className="flex gap-2">
            <dt className="shrink-0">Sends at:</dt>
            <dd className="break-words min-w-0">{scheduleLabel}</dd>
          </div>
        )}
      </dl>
      <p className="text-sm text-muted-foreground">
        Messages go out to real customers and cannot be recalled once sent.
      </p>
      <div className="flex flex-wrap justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={sending}
          className="px-4 py-2 text-sm border border-input rounded-lg hover:bg-muted/50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={sending}
          className="px-4 py-2 text-sm text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50"
        >
          {sending ? (scheduleLabel ? 'Scheduling...' : 'Sending...') : scheduleLabel ? 'Schedule' : 'Send now'}
        </button>
      </div>
    </div>
  </Modal>
);

/** Step 4: name, a test message to the admin's own phone, then the confirmed send. */
const SendStep = ({ wizard }) => {
  const { state, patch, template, preview, draft, sendBlock, lookups, onSent } = wizard;
  const [confirming, setConfirming] = useState(false);
  const busy = draft.testing || draft.sending;
  const previewCount = preview.data?.sendable_count ?? 0;
  // Once a draft exists, its count is the truth: the server built the list when it was created.
  const hasDraft = draft.queued !== null;
  const count = hasDraft ? draft.queued : previewCount;
  const audienceChanged = hasDraft && draft.queued !== previewCount;
  const nobody = hasDraft && draft.queued === 0;
  const name = state.name.trim();
  const senderMissing = senderProblem(state, template);
  const phone = state.testPhone.trim();
  const numberReady = phone !== '' && (phone.startsWith('+') || state.testCountry !== '');
  const sourceLabel = SOURCE_OPTIONS.find((o) => o.value === state.source)?.label;

  const [when, setWhen] = useState('now'); // 'now' | 'later'
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [scheduleZone, setScheduleZone] = useState(browserTimeZone());
  const scheduleReady = when === 'now' || (scheduleDate !== '' && scheduleTime !== '');
  let scheduledIso = null;
  let scheduleLabel = null;
  if (when === 'later' && scheduleReady) {
    try {
      scheduledIso = zonedTimeToUtcIso(scheduleDate, scheduleTime, scheduleZone);
      scheduleLabel = `${scheduleDate} ${scheduleTime} (${scheduleZone})`;
    } catch {
      scheduledIso = null; // an unrecognized date/time string; the button stays disabled
    }
  }
  // Caught here so a stale tab (opened well before the chosen time) is blocked before the request
  // is even sent, not just after the backend rejects it - same guarantee as the 400 handling below.
  const scheduleInPast = scheduledIso !== null && new Date(scheduledIso).getTime() <= Date.now();

  const confirm = async () => {
    if (when === 'later' && scheduledIso) {
      await draft.schedule(scheduledIso);
    } else {
      await draft.send();
    }
    setConfirming(false);
  };

  return (
    <div className="space-y-5">
      {sendBlock && (
        <div
          data-testid="send-disabled-notice"
          role="status"
          className="px-4 py-3 space-y-2 text-sm text-amber-900 dark:text-amber-300 border border-amber-200 dark:border-amber-900 rounded-lg bg-amber-50 dark:bg-amber-950/50"
        >
          <p>{sendBlock}</p>
          {lookups.status.error && (
            <button
              type="button"
              onClick={lookups.status.reload}
              className="px-3 py-1 bg-card border border-amber-300 dark:border-amber-800 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/60"
            >
              Check again
            </button>
          )}
        </div>
      )}

      <div>
        <label htmlFor="wa-campaign-name" className="block mb-1 text-sm text-foreground/80">
          Campaign name
        </label>
        <input
          id="wa-campaign-name"
          aria-label="Campaign name"
          value={state.name}
          maxLength={255}
          disabled={busy}
          onChange={(e) => patch({ name: e.target.value })}
          className={fieldClass}
        />
        {!name && <p className="mt-1 text-sm text-red-700 dark:text-red-300">Give the campaign a name</p>}
      </div>

      <div className="flex gap-2">
        <div className="flex-1">
          <label htmlFor="wa-sender-name" className="block mb-1 text-sm text-foreground/80">
            Sender name
          </label>
          <input
            id="wa-sender-name"
            aria-label="Sender name"
            value={state.senderName}
            maxLength={255}
            disabled={busy}
            onChange={(e) => patch({ senderName: e.target.value })}
            className={fieldClass}
          />
        </div>
        <div className="flex-1">
          <label htmlFor="wa-sender-position" className="block mb-1 text-sm text-foreground/80">
            Sender&apos;s position
          </label>
          <input
            id="wa-sender-position"
            aria-label="Sender's position"
            value={state.senderPosition}
            maxLength={255}
            disabled={busy}
            onChange={(e) => patch({ senderPosition: e.target.value })}
            className={fieldClass}
          />
        </div>
      </div>
      {senderMissing && <p className="-mt-3 text-sm text-red-700 dark:text-red-300">{senderMissing}</p>}

      <dl
        data-testid="send-summary"
        className="p-4 space-y-1 text-sm text-foreground/80 border border-border rounded-lg bg-muted/50"
      >
        <div className="flex gap-2">
          <dt className="shrink-0 text-muted-foreground">Recipients:</dt>
          <dd>
            {hasDraft
              ? plural(count, 'recipient', 'recipients')
              : `Preview: ${plural(count, 'recipient', 'recipients')}`}
          </dd>
        </div>
        <div className="flex gap-2">
          <dt className="shrink-0 text-muted-foreground">Template:</dt>
          <dd className="min-w-0 break-words">{template?.name}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="shrink-0 text-muted-foreground">Audience:</dt>
          <dd>{sourceLabel}</dd>
        </div>
      </dl>

      {audienceChanged && (
        <div
          data-testid="audience-changed-warning"
          role="alert"
          className="px-4 py-3 text-sm font-medium text-red-900 dark:text-red-300 border border-red-300 dark:border-red-800 rounded-lg bg-red-50 dark:bg-red-950/50"
        >
          {`The audience changed since you previewed it: ${plural(draft.queued, 'person', 'people')} will receive this (preview showed ${previewCount}).`}
        </div>
      )}
      {draft.mayHaveStarted && (
        <div
          role="alert"
          className="px-4 py-3 space-y-2 text-sm text-red-900 dark:text-red-300 border border-red-300 dark:border-red-800 rounded-lg bg-red-50 dark:bg-red-950/50"
        >
          <p>{MAY_HAVE_STARTED}</p>
          <button
            type="button"
            onClick={() => onSent?.(draft.draft.id)}
            className="px-3 py-1 bg-card border border-red-300 dark:border-red-800 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/60"
          >
            Open campaign
          </button>
        </div>
      )}

      <div className="space-y-2">
        <p className="text-sm text-foreground/80">
          Send a test to your own WhatsApp number first. It goes out for real.
        </p>
        <div className="flex gap-2">
          <CountrySelect
            variant="phone"
            value={state.testCountry}
            onChange={(iso) => patch({ testCountry: iso })}
            ariaLabel="Test number country"
          />
          <input
            type="tel"
            aria-label="Your WhatsApp number"
            value={state.testPhone}
            disabled={busy}
            onChange={(e) => patch({ testPhone: e.target.value })}
            placeholder="Your number"
            className={`${fieldClass} min-w-0`}
          />
        </div>
        <button
          type="button"
          disabled={
            Boolean(sendBlock) || busy || draft.mayHaveStarted || !name || Boolean(senderMissing) || !numberReady || !template
          }
          onClick={() => draft.sendTest({ phone, country: phone.startsWith('+') ? '' : state.testCountry })}
          className="px-4 py-2 text-sm border border-input rounded-lg hover:bg-muted/50 disabled:opacity-50"
        >
          {draft.testing ? 'Sending test...' : 'Send test to my number'}
        </button>
        {draft.testError && !draft.tested && (
          <p className="text-sm text-red-700 dark:text-red-300">{draft.testError}</p>
        )}
        {draft.tested && (
          <p className="flex items-center gap-2 text-sm text-green-800 dark:text-green-300">
            <CheckCircle2 size={16} /> Test message sent. Check your phone, then send the campaign.
          </p>
        )}
      </div>

      <div className="space-y-2">
        <p className="text-sm text-foreground/80">When should this go out?</p>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => setWhen('now')}
            className={`px-4 py-2 text-sm rounded-lg border ${when === 'now' ? 'bg-primary text-primary-foreground border-foreground' : 'border-input hover:bg-muted/50'}`}
          >
            Send now
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => setWhen('later')}
            className={`px-4 py-2 text-sm rounded-lg border ${when === 'later' ? 'bg-primary text-primary-foreground border-foreground' : 'border-input hover:bg-muted/50'}`}
          >
            Schedule for later
          </button>
        </div>
        {when === 'later' && (
          <div className="flex flex-wrap gap-2">
            <input
              type="date"
              aria-label="Schedule date"
              value={scheduleDate}
              disabled={busy}
              onChange={(e) => setScheduleDate(e.target.value)}
              className={`${fieldClass} w-auto`}
            />
            <input
              type="time"
              aria-label="Schedule time"
              value={scheduleTime}
              disabled={busy}
              onChange={(e) => setScheduleTime(e.target.value)}
              className={`${fieldClass} w-auto`}
            />
            <select
              aria-label="Schedule timezone"
              value={scheduleZone}
              disabled={busy}
              onChange={(e) => setScheduleZone(e.target.value)}
              className={`${fieldClass} w-auto`}
            >
              {TIME_ZONES.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
          </div>
        )}
        {when === 'later' && scheduleInPast && (
          <p className="text-sm text-red-700 dark:text-red-300">Pick a time in the future.</p>
        )}
      </div>

      <div className="space-y-2">
        <button
          type="button"
          disabled={
            Boolean(sendBlock) ||
            busy ||
            !draft.tested ||
            draft.mayHaveStarted ||
            nobody ||
            !name ||
            Boolean(senderMissing) ||
            (when === 'later' && (!scheduledIso || scheduleInPast))
          }
          onClick={() => setConfirming(true)}
          className="px-5 py-2 text-sm text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50"
        >
          {when === 'later' ? 'Schedule campaign' : 'Send campaign'}
        </button>
        {nobody && (
          <p className="text-sm text-red-700 dark:text-red-300">
            This draft has nobody to message, so it cannot be sent. Go back and change the audience.
          </p>
        )}
        {!draft.tested && (
          <p className="text-sm text-muted-foreground">
            Send a test message first. Changing anything after a test means testing again.
          </p>
        )}
      </div>

      {confirming && (
        <ConfirmSend
          count={count}
          templateName={template?.name}
          campaignName={name}
          sending={draft.sending}
          scheduleLabel={when === 'later' ? scheduleLabel : null}
          onCancel={() => setConfirming(false)}
          onConfirm={confirm}
        />
      )}
    </div>
  );
};

export default SendStep;
